import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePopoutMeetingState } from '../hooks/usePopoutMeetingState';

/**
 * Full-page screen share view for pop-out window.
 */
export default function ScreenSharePopoutPage() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const {
    participants,
    localParticipant,
    screenShareOwnerId,
    screenShareTrack,
    isConnected,
    isSynced,
  } = usePopoutMeetingState();

  const sharerName = useMemo(() => {
    if (!screenShareOwnerId) return '';

    const sharer =
      localParticipant?.participantId === screenShareOwnerId
        ? localParticipant
        : participants.find((p) => p.participantId === screenShareOwnerId);

    return sharer?.displayName || 'Participant';
  }, [participants, localParticipant, screenShareOwnerId]);

  const screenShareStream = useMemo(() => {
    if (!screenShareTrack) return null;
    return new MediaStream([screenShareTrack]);
  }, [screenShareTrack]);

  if (!isSynced || !isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-3 border-accent border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Syncing with meeting...</p>
          <p className="text-sm text-gray-500 mt-2">
            Keep your main meeting tab open
          </p>
        </div>
      </div>
    );
  }

  if (!screenShareStream) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <NoScreenShareIcon />
          <p className="text-gray-400 mt-4">No active screen share</p>
          <p className="text-sm text-gray-500 mt-2">
            This window will update when someone shares their screen
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <header className="flex items-center justify-between px-4 py-2 bg-bg-primary/80 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <ScreenShareActiveIcon />
          <span className="text-sm text-gray-300">
            {sharerName} is sharing their screen
          </span>
        </div>
        <span className="text-sm text-gray-500">{roomCode}</span>
      </header>

      <div className="flex-1 flex items-center justify-center p-2">
        <ScreenShareVideo stream={screenShareStream} />
      </div>
    </div>
  );
}

interface ScreenShareVideoProps {
  stream: MediaStream;
}

/**
 * Video element for screen share stream.
 */
function ScreenShareVideo({ stream }: ScreenShareVideoProps) {
  return (
    <video
      ref={(el) => {
        if (el && el.srcObject !== stream) {
          el.srcObject = stream;
        }
      }}
      autoPlay
      playsInline
      className="max-w-full max-h-full object-contain rounded-lg"
    />
  );
}

/**
 * Icon shown when no screen share is active.
 */
function NoScreenShareIcon() {
  return (
    <svg className="w-16 h-16 text-gray-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

/**
 * Icon for active screen share.
 */
function ScreenShareActiveIcon() {
  return (
    <svg className="w-5 h-5 text-success" fill="currentColor" viewBox="0 0 20 20">
      <path d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" />
    </svg>
  );
}
